'use client'

import { forwardRef, useImperativeHandle, useRef } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import Image from 'next/image'
import { X } from 'lucide-react'
import { useDebouncedCallback } from 'use-debounce'

interface SearchInputProps {
  onChange: (value: string) => void
  value: string
  placeholder?: string
}

interface ForwardRefProps {
  focus: () => void
}
const SearchInput = forwardRef<ForwardRefProps, SearchInputProps>(
  ({ onChange, value, placeholder }, ref) => {
    const inputRef = useRef<HTMLInputElement | null>(null)

    useImperativeHandle(ref, () => ({
      focus: () => {
        inputRef?.current?.focus()
      },
    }))

    const handleOnChange = useDebouncedCallback((value) => {
      onChange(value.trim())
    }, 300)

    const handleRemoveText = () => {
      if (!inputRef.current) return

      inputRef.current.value = ''
      onChange(inputRef.current.value)
    }

    return (
      <div className="flex w-full items-center gap-3 border-[calc(0.6px)] border-primary-600 bg-white px-4 shadow-search">
        <Image
          priority
          src={'/icons/search.svg'}
          height={24}
          width={24}
          alt={'Search'}
          className={`size-6`}
        />

        <Input
          placeholder={placeholder}
          onChange={(e) => handleOnChange(e.currentTarget.value)}
          className="h-12 border-none px-0 py-3.5 placeholder:text-base focus-visible:ring-0 focus-visible:ring-offset-0"
          ref={inputRef}
        />

        {value && (
          <Button
            variant={'plain'}
            size={'plain'}
            onClick={() => handleRemoveText()}
          >
            <X className="size-6 cursor-pointer" strokeWidth={1} />
          </Button>
        )}
      </div>
    )
  },
)
SearchInput.displayName = 'SearchInput'

export default SearchInput
